import { Switch, Text, toast } from "@medusajs/ui"
import { ErrorState, Section } from "@/components/ui"
import { useCan } from "@/lib/permissions"
import { errorMessage } from "@/lib/sdk"
import { useAccountSettings, useSaveAccountSettings } from "@/lib/settings"
import { SettingsTitle } from "./settings-general"

/** Settings → Customer accounts. Sign-in is passwordless: an emailed one-time code. */
export function AccountSettingsPage() {
  const { data, error } = useAccountSettings()
  const save = useSaveAccountSettings()
  const writable = useCan()("settings:write")
  if (error) return <ErrorState error={error} />
  if (!data) return <Text size="small">Loading…</Text>
  const on = data.settings.enabled
  const toggle = (enabled: boolean) => save.mutate({ enabled }, { onSuccess: () => toast.success(enabled ? "Customer accounts turned on" : "Customer accounts turned off"), onError: (e) => toast.error(errorMessage(e)) })
  return (
    <div className="flex flex-col gap-4 pb-24">
      <SettingsTitle title="Customer accounts" />
      <Section title="Sign-in">
        <div className="flex items-start justify-between gap-4 p-4">
          <div>
            <Text size="small" weight="plus">Let customers sign in to see their orders</Text>
            <Text size="small" className="mt-1 text-ui-fg-subtle">
              Customers enter their email and get a 6-digit code. There are no passwords to forget or leak. Checkout always works without an account.
            </Text>
          </div>
          <Switch checked={on} disabled={!writable || save.isPending} onCheckedChange={toggle} aria-label="Customer accounts" />
        </div>
      </Section>
      <Section title="How it works">
        <ul className="flex list-disc flex-col gap-1.5 p-4 pl-8 text-[13px] text-ui-fg-subtle">
          <li>Codes expire after 10 minutes and stop working after 5 wrong tries.</li>
          <li>The sign-in form never reveals whether an email has an account.</li>
          <li>Customers see their own orders, status and default address on the Account page.</li>
          <li>Turning this off hides the Account link and ends all customer sessions.</li>
        </ul>
      </Section>
    </div>
  )
}
